import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform } from 'ionic-angular';
import {
  OnSiteMovementEditor, OnSiteMovementPerLegEditor,
  OnSiteMovementStatuses,
  OnSiteMovementType
} from '../../../models/on-site-movement/on-site-movement.models';
import { LockDayFilters } from '../../../models/lock-day.models';
import { KnownUser } from '../../../models/user-models';
import { JackingPermissions, LockedDayPermissions } from '../../../models/permissions/permission.models';
import { LocationOption } from '../../../models/setup/setup.models';
import { DailyLogOperationInstance, DailyOperationLogRecord } from '../../../models/projects/operation.models';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { AppData } from '../../../models/app-data/app-data';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { UserService } from '../../../services/user/user.service';
import { MetadataService } from '../../../services/metadata/metadata-service.service';
import { DateService } from '../../../services/date/date.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { SetupService } from '../../../services/setup/setup.service';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { OperationSaveService } from '../../../services/operations/operation-save/operation-save.service';
import { OnSiteMovementService } from '../../../services/on-site-movement/on-site-movement.service';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { OnSiteMovementEditCompletePage } from '../on-site-movement-edit-complete/on-site-movement-edit-complete';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { ActionType, ErrorType, ValidationResult } from '../../../models/projects/validation.models';
import { ErrorsComponent } from '../../shared/components/errors/errors.component';
import { OperationTimeService } from '../../../services/operations/operation-time/operation-time.service';
import { cloneDeep } from 'lodash';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';

@IonicPage()
@Component({
  selector: 'page-on-site-movement-edit',
  templateUrl: 'on-site-movement-edit.html'
})
export class OnSiteMovementEditPage {
  config = AppConfig.settings;

  public isLoading: boolean;
  public isSaving: boolean;
  public isReadonly: boolean;
  public isWeb: boolean;

  public minDate: DateTime;
  public maxDate: DateTime;

  public recordSpansMultipleDays: boolean;
  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  public dailyLogDate: PtLuxonDate;
  public editor: OnSiteMovementEditor;

  public jackingEngineers: KnownUser[] = [];
  public onSiteMovementTypes: OnSiteMovementType[] = [];
  public locations: LocationOption[] = [];

  // Legs displayed in template order (DisplayOrder if provided, else LegNumber)
  public displayLegs: OnSiteMovementPerLegEditor[] = [];

  private permissions: JackingPermissions = new JackingPermissions();
  private lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();
  private operation: DailyLogOperationInstance;
  private isAnyDayNotLockedByCurrentUser: boolean;

  validationResult: ValidationResult;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public platform: Platform,
    private onSiteMovementService: OnSiteMovementService,
    private permissionService: PermissionsService,
    private userService: UserService,
    private metadataService: MetadataService,
    private dateService: DateService,
    private lockedDayService: LockedDayService,
    private setupService: SetupService,
    private operationService: OperationService,
    private operationTimeService: OperationTimeService,
    private dailyLogService: DailyLogService,
    private operationSaveService: OperationSaveService,
    private appDataSyncService: AppDataSyncService,
    private timeZoneService: TimeZoneService,
    private modalController: ModalController,
  ) {
    this.dailyLogDate = navParams.get('dailyLogDate');
  }

  public ngOnInit() {
    this.isLoading = true;
    this.validationResult = new ValidationResult();
    this.isWeb = this.platform.is('core');
    this.ionViewEnter();
  }

  public async ionViewEnter() {
    this.editor = this.navParams.get('onSiteMovementRecord');

    if (!this.editor) {
      throw Error('On Site Movement Record not found');
    }

    this.minDate = this.editor.startDate.dateTime;
    this.maxDate = await this.dateService.addDuration(this.minDate, { day: 1 }, true);

    await Promise.all([
      this.loadJackingPermissions(),
      this.loadUsers(),
      this.loadOnSiteMovementTypes(),
      this.loadLocationOptions(),
      this.checkIfAnyDaysNotLockedByUser(),
      this.calculateIfRecordSpansMultipleDays(),
      this.loadLinkedOperation(),
    ]);

    // Ensure legs exist + order for UI
    this.initialiseLegs();
    this.buildDisplayLegs();

    this.isReadonly = this.navParams.get('readOnly')
      || !this.permissions.canManageJackingRecords
      || !this.lockedDayPermissions.functionalPermissions.canImpactCurrentLockedDay
      || this.isAnyDayNotLockedByCurrentUser;

    this.isLoading = false;
  }

  private initialiseLegs() {
    if (!this.editor.legs) {
      this.editor.legs = [];
    }

    // Determine how many legs this record uses.
    // If the API always returns all legs, you can simplify this to 4 or 6 as appropriate.
    const maxLegFromData = this.editor.legs.reduce((m, x) => Math.max(m, x.legNumber || 0), 0);
    const legCount = maxLegFromData > 0 ? maxLegFromData : 4;

    for (let legNo = 1; legNo <= legCount; legNo++) {
      if (!this.editor.legs.some(x => x.legNumber === legNo)) {
        this.editor.legs.push({
          id: 0,
          legNumber: legNo,
          load: null,
          ext: null,
          displayOrder: null
        });
      }
    }
  }

  private buildDisplayLegs() {
    const legs = (this.editor?.legs ?? []).slice();

    // Sort by template DisplayOrder if present, otherwise by LegNumber
    this.displayLegs = legs.sort((a, b) => {
      const ao = a.displayOrder;
      const bo = b.displayOrder;

      const aHas = ao != null;
      const bHas = bo != null;

      if (aHas && bHas) {
        return ao - bo;
      }

      if (aHas && !bHas) {
        return -1;
      }

      if (!aHas && bHas) {
        return 1;
      }

      return (a.legNumber ?? 0) - (b.legNumber ?? 0);
    });
  }

  private async loadUsers() {
    this.userService.getKnownUsers().then(users => {
      users = users.filter(u => u.roles.some(r => r.permissions.some(p => p.name === 'Add/Amend Jacking Records')));
      this.jackingEngineers = users;
    });
  }

  private async loadOnSiteMovementTypes() {
    this.metadataService.getOnSiteMovementTypes().then(types => {
      this.onSiteMovementTypes = types;
    });
  }

  private async loadJackingPermissions() {
    this.permissions = await this.permissionService.jackingPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
  }

  private async loadLocationOptions() {
    this.setupService.getSetupMetadata(this.editor.vesselId, this.dailyLogDate.dateTime).then(metadata => {
      this.setupService.getLocalSetupSummary().then(setupSummary => {
        const projectOptions = metadata.projectOptions;
        const project = projectOptions.find(e => e.id === setupSummary.projectId);
        const phaseOptions = project ? project.phaseOptions : projectOptions[0].phaseOptions;
        const phase = phaseOptions.find(e => e.id === setupSummary.phaseId);
        const stageOptions = phase ? phase.stageOptions : phaseOptions[0].stageOptions;
        const stage = stageOptions.find(e => e.id === setupSummary.stageId);
        this.locations = stage ? stage.locationOptions : stageOptions[0].locationOptions;
      });
    });
  }

  private async calculateIfRecordSpansMultipleDays() {
    this.recordSpansMultipleDays = this.editor.startDay !== this.editor.endDay;
  }

  private async checkIfAnyDaysNotLockedByUser() {
    let filterDay = this.minDate;
    const lockDayFilters = new LockDayFilters();
    while (!this.isAnyDayNotLockedByCurrentUser && filterDay <= this.maxDate) {
      lockDayFilters.date = filterDay;
      const summary = await this.lockedDayService.checkDay(lockDayFilters);
      this.isAnyDayNotLockedByCurrentUser = summary.locked && !summary.lockedByYou;
      filterDay = await this.dateService.addDuration(filterDay, { day: 1 }, true);
    }
  }

  private async loadLinkedOperation() {
    this.operation = await this.operationService.getProjectSpecificOperationInstance(this.editor.operationId);

    if (!this.operation) {
      this.operation = await this.operationService.getProjectSpecificOperationInstance(this.editor.localOperationId);
    }

    const appData = new AppData();
    appData.dailyLogOperationInstances = [this.operation];
    await this.appDataSyncService.refreshTimesToShow(appData);
  }

  public compareOnSiteMovementTypes(t1: OnSiteMovementType, t2: OnSiteMovementType): boolean {
    return t1.id === t2.id;
  }

  public async toggleStartDateConfirmed() {
    this.editor.startDateConfirmed = !this.editor.startDateConfirmed;
    this.operation.startConfirmed = this.editor.startDateConfirmed;
  }

  public async toggleEndDateConfirmed() {
    this.editor.endDateConfirmed = !this.editor.endDateConfirmed;
    this.operation.endConfirmed = this.editor.endDateConfirmed;
  }

  public async onStartTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(this.editor.startDateAsTimeString, this.editor.startDate.dateTime);
    if (allowedTime) {
      if (!offsetSelected) {
        this.editor.startDate = await this.dateService.setPtLuxonDateFromUserEnteredString(this.editor.startDateAsTimeString, this.editor.startDate.dateTime);
      } else {
        this.editor.startOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      if (this.editor.startDate.offsetNull()) {
        return;
      }

      if (!offsetSelected) {
        this.editor.startOffset = await this.timeZoneService.offsetForTime(
          this.editor.startDate.getDate()
        );
      }

      this.editor.startDateAsDateTimeString = await this.dateService.setFullTimeString(this.editor.startDate.dateTime);
      this.editor.startDay = await this.editor.startDate.getDay();
      this.editor.startOffset = await this.timeZoneService.offsetForTime(this.editor.startDate.dateTime);

      this.operation.startTime = cloneDeep(this.editor.startDate);
      this.operation.startTimeToShow = cloneDeep(this.editor.startDateAsTimeString);
      this.operation.startTimeOffset = cloneDeep(this.editor.startOffset);

      this.checkIfTimeIsLost(this.operation.startTimeToShow,
        this.operation.startTime,
        'Start Time');

      Promise.all([
        this.checkIfAnyDaysNotLockedByUser(),
        this.calculateIfRecordSpansMultipleDays()
      ]);
    }
  }

  public async onEndTimeChange(offsetSelected?: boolean, offsetSelectedPtDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(this.editor.endDateAsTimeString, this.editor.endDate.dateTime);
    if (allowedTime) {
      if (!offsetSelected) {
        this.editor.endDate = await this.dateService.setPtLuxonDateFromUserEnteredString(this.editor.endDateAsTimeString, this.editor.endDate.dateTime);
      } else {
        this.editor.endOffset = offsetSelectedPtDate.dateTime.offset / 60;
      }

      if (this.editor.endDate.offsetNull()) {
        return;
      }

      if (!offsetSelected) {
        this.editor.endOffset = await this.timeZoneService.offsetForTime(this.editor.endDate.getDate());
      }

      this.editor.endDateAsDateTimeString = await this.dateService.setFullTimeString(this.editor.endDate.dateTime);
      this.editor.endDay = await this.editor.endDate.getDay();
      this.editor.endOffset = await this.timeZoneService.offsetForTime(this.editor.endDate.getDate());

      this.operation.endTime = cloneDeep(this.editor.endDate);
      this.operation.endTimeToShow = cloneDeep(this.editor.endDateAsTimeString);
      this.operation.endTimeOffset = cloneDeep(this.editor.endOffset);

      this.checkIfTimeIsLost(this.operation.endTimeToShow,
        this.operation.endTime,
        'End Time');

      Promise.all([
        this.checkIfAnyDaysNotLockedByUser(),
        this.calculateIfRecordSpansMultipleDays()
      ]);
    }
  }

  public async onKeyPressValidateDecimals(currentValue: number, numberOfDecimals: number, event: KeyboardEvent, allowNegative?: boolean) {
    const allowedSpecialKeys: number[] = [8, 9, 13, 37, 35, 36, 37, 39, 45, 46, 91, 92, 144, 145];
    if (allowedSpecialKeys.indexOf(event.which) !== -1) {
      return;
    }

    if ((event.which === 65 && event.ctrlKey === true) ||
      (event.which === 67 && event.ctrlKey === true) ||
      (event.which === 86 && event.ctrlKey === true) ||
      (event.which === 88 && event.ctrlKey === true) ||
      (event.which === 65 && event.metaKey === true) ||
      (event.which === 67 && event.metaKey === true) ||
      (event.which === 86 && event.metaKey === true) ||
      (event.which === 88 && event.metaKey === true)
    ) {
      return;
    }

    if (
      (numberOfDecimals <= 0 && event.key === '.') ||
      (currentValue && currentValue.toString().indexOf('.') > -1 && event.key === '.')
    ) {
      event.preventDefault();
    }

    if (currentValue != null) {
      const afterDecimal = currentValue.toString().split('.')[1];
      if (afterDecimal != null) {
        if (afterDecimal.length >= numberOfDecimals) {
          event.preventDefault();
        }
      }
    }

    if (
      (event.shiftKey || (event.which < 48 || event.which > 57)) &&
      ((event.which < 96 || event.which > 105)) &&
      event.which !== 110 &&
      event.which !== 190) {
      if (allowNegative && event.key === '-' && (!currentValue || !currentValue.toString().includes('-'))) {
        return;
      }

      event.preventDefault();
    }
  }

  public async saveInProgress() {
    this.onSiteMovementService.setStatus(this.editor, OnSiteMovementStatuses.InProgress);
    await this.save();
  }

  public async complete() {
    const validationResult = await this.onSiteMovementService.validateRecord(this.editor);
    if (validationResult.isValid) {
      this.onSiteMovementService.setStatus(this.editor, OnSiteMovementStatuses.Complete);
      this.save();
    } else {
      const _self = this;

      const saveCallback = function (editor: OnSiteMovementEditor, cancel: boolean) {
        return new Promise((resolve, reject) => {
          if (cancel) {
            resolve(null);
          } else {
            _self.editor = editor;
            _self.onSiteMovementService.setStatus(_self.editor, OnSiteMovementStatuses.Complete);
            _self.save();
            resolve(null);
          }
        });
      };

      this.navCtrl.push(OnSiteMovementEditCompletePage,
        { editor: this.editor, validationResult: validationResult, callback: saveCallback },
        { animation: 'ios-transition' });
    }
  }

  private async save() {
    if (this.isSaving) {
      return;
    } else {
      this.isSaving = true;

      const appDataBlob = new AppData();
      appDataBlob.cleanseLogs = false;

      appDataBlob.onSiteMovementRecords = [this.editor];

      let existingOperationDailyLogs: DailyOperationLogRecord[] = [];
      existingOperationDailyLogs = await this.appDataSyncService.getLogsForOperation(this.operation.id);

      const newOperationDailyLogs = await this.operationSaveService.saveOperation(
        this.operation,
        this.operation.stepCollectionInstance?.stepInstances,
        existingOperationDailyLogs,
        null,
        true);

      appDataBlob.dailyLogOperationInstances = [this.operation];
      appDataBlob.dailyLogRecords = newOperationDailyLogs;

      await this.appDataSyncService.syncLocalData(appDataBlob);

      this.isSaving = false;
      this.navCtrl.pop();
    }
  }

  openErrorModal() {
    const errorModal = this.modalController.create(ErrorsComponent, {
      validationResult: this.validationResult,
      refresh: true
    });
    errorModal.present();
  }

  private async checkIfTimeIsLost(timeInput: string, ptDate: PtLuxonDate, timeLabel: string) {
    const lostInTime = ptDate.isLostInTime;

    const errIndex = this.validationResult.errors.findIndex(err => err.message.includes(`${timeLabel}`));
    if (errIndex !== -1) {
      this.validationResult.errors.splice(errIndex, 1);

      if (this.validationResult.errors.length === 0) {
        this.validationResult.isValid = true;
      }
    }

    if (lostInTime && lostInTime.result) {
      this.validationResult
        .addError(`The time '${timeInput}' entered for '${timeLabel}' is invalid due to a
        timezone change from '${lostInTime.timeZone.timeOfChangeToShow}' to '${lostInTime.timeZone.dateToShow}'.`,
          ErrorType.Error,
          ActionType.Timezone);
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/on-site-movement/on-site-movement-edit/on-site-movement-edit.ts